import { cn } from '@realtyfeed/utils';
import { clsx } from 'clsx';
import type { ReactNode } from 'react';
import { ChevronDown, ChevronRight } from 'react-feather';
import { focusRingClass } from '../focusRing';

/**
 * The sidebar row *skin* from the design comp, split into a class helper and a
 * body so each portal can keep its own row semantics.
 *
 * The two portals do not render the same element for a row — data-consumer
 * gates items on package availability and intercepts the click, data-provider
 * links straight through, and both render `<button>` for a group parent — so a
 * single `<SidebarNavRow>` component would need every one of those variants as
 * props. Instead the shared thing is what the comp actually pins down: the
 * 48px pill, its `#21376F` active fill, and the icon/label/chevron rhythm.
 *
 * Colours are the sampled comp values (`gdx.*` in the Tailwind preset), not
 * approximations, except the idle label: that one is the design system's own
 * `ds-text-primary` (`#13113F` in both portals' light modes) rather than the
 * sampled `#4E4C6F`, so an unselected row reads in the same ink as body copy.
 * Active pill `#21376F` with white text,
 * and `#F0F1F6` behind an *ancestor* of the active row (`nav-branch`) so the
 * path down to a selected sub-category reads as a hierarchy rather than as two
 * unrelated selections.
 */

type SidebarRowState = {
  active?: boolean;
  /**
   * This row is not the current page, but a descendant of it is — a group whose
   * sub-item is selected. Deliberately a separate flag from `active` rather than
   * a shade of it: only one row in the nav is ever `active`, while every
   * ancestor on the way to it is `branch`.
   */
  branch?: boolean;
  /** Locked/unavailable item — greyed, still rendered so the nav shape is stable. */
  muted?: boolean;
  collapsed?: boolean;
};

export function sidebarRowClass(
  { active, branch, muted, collapsed }: SidebarRowState,
  className?: string
) {
  return cn(
    'group flex w-full items-center rounded-lg py-3.5 text-sm font-medium transition-colors',
    // Keyboard traversal of the nav: see `focusRing` for why the shell needs its own ring.
    focusRingClass('sidebar'),
    collapsed ? 'justify-center gap-0 px-2' : 'gap-3 px-3',
    active && 'bg-ds-action-primary text-white hover:bg-ds-action-primary-hover',
    // The tint carries through hover, so pointing at an already-lit group does
    // not read as un-selecting it.
    !active &&
      branch &&
      !muted &&
      'bg-ds-bg-subtle text-ds-text-primary hover:bg-ds-bg-subtle hover:text-ds-text-accent dark:bg-slate-800 dark:text-slate-200 dark:hover:text-white',
    !active &&
      !branch &&
      !muted &&
      'text-ds-text-primary hover:bg-gdx-indigo-tint hover:text-ds-text-accent dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:text-white',
    muted && 'cursor-not-allowed text-gdx-nav-muted hover:bg-transparent dark:text-slate-500',
    className
  );
}

export type SidebarRowContentProps = SidebarRowState & {
  icon?: ReactNode;
  label: ReactNode;
  /**
   * `link` is the comp's static caret on a navigable row, `expand` the
   * rotating one on a group parent, `none` for plain rows.
   */
  chevron?: 'none' | 'link' | 'expand';
  expanded?: boolean;
  /** Extra glyph before the chevron — the consumer portal's lock, for instance. */
  trailing?: ReactNode;
  /**
   * Keep the label on one line, ellipsising if it still does not fit, instead of
   * wrapping it onto a second. A wrapped row grows past the comp's 48px pill, so
   * a nav with a few long titles reads as a ragged stack of different-height
   * rows — which is why data-consumer opts in. data-provider stays on wrapping:
   * its "GDX Onboarding / Access Requests" is too long for one line at any
   * sidebar width worth having, so there an ellipsis would hide the half of the
   * title that identifies the page.
   */
  noWrap?: boolean;
};

export function SidebarRowContent({
  icon,
  label,
  collapsed,
  active,
  chevron = 'none',
  expanded,
  trailing,
  noWrap,
}: SidebarRowContentProps) {
  return (
    <>
      {icon && <span className="flex h-5 w-5 shrink-0 items-center justify-center">{icon}</span>}

      {!collapsed && (
        <>
          {/* `min-w-0` so the ellipsis can actually engage: a flex item's
              min-width is `auto`, which would let the text push the row wider
              than the column instead of clipping. */}
          <span
            className={clsx(
              'min-w-0 flex-1 text-start leading-tight',
              noWrap ? 'truncate' : 'whitespace-normal'
            )}
          >
            {label}
          </span>
          {trailing}
          {chevron === 'link' && (
            <ChevronRight
              size={16}
              className={clsx('shrink-0', active ? 'text-white/80' : 'text-gdx-nav-muted')}
              aria-hidden
            />
          )}
          {chevron === 'expand' && (
            <ChevronDown
              size={16}
              className={clsx(
                'shrink-0 transition-transform',
                expanded && 'rotate-180',
                active ? 'text-white/80' : 'text-gdx-nav-muted'
              )}
              aria-hidden
            />
          )}
        </>
      )}
    </>
  );
}
